// ----------------------------------------------------------------------
// <copyright file="Izenda.SubscriptionDetails.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  const {
    filter,
    isEmpty,
    map,
    isEqual,
    omit,
    trim,
    uniqueId,
    chain,
    union,
    indexOf,
    isArray,
    split,
    find,
    compact
  } = require('lodash/fp');

  var m = require('mobx');
  var uiState = require('logic/Izenda.UiState');
  var trackable = require('share/tracking/Izenda.Trackable');
  var TimezoneConstant = require('logic/data/Izenda.TimezoneConstants');
  var momentTimezone = require('moment-timezone');
  var Constant = require('logic/data/Izenda.ReportSubscriptionConstants');
  var DashboardConstant = require('logic/data/Izenda.DashboardSchedulingConstants');
  var ObjectStateConstants = require('logic/data/Izenda.ObjectStateConstants');
  var ScheduleConstants = require('logic/data/Izenda.ScheduleConstants');
  var SCHEDULE_SEARCH_CONSTANTS = require('logic/data/Izenda.SchedulingSearchConstants');
  var AppGlobalConstants = require('logic/data/Izenda.AppGlobalConstants');
  var DATE_FORMAT = AppGlobalConstants.DATE_FORMAT;

  function SubscriptionDetails(data, emailTemplates, reportFilter) {
    this.tenantId = uiState.tenantId;

    var props = {
      name: null,
      schedule: null,
      filterValueSelection: null,
      type: null,
      timeZoneName: null,
      timeZoneValue: null,
      startDate: null,
      startTime: null,
      recurrenceType: null,
      recurrencePattern: null,
      recurrencePatternSetting: null,
      dailyRecurrencePatternSetting: {
        isEveryWeekday: false,
        recurrenceDay: 1
      },
      weeklyRecurrencePatternSetting: {
        recurrenceWeek: 1,
        selectedDayValue: find(function(d) {
          return d.label === momentTimezone().format('dddd');
        }, ScheduleConstants.DAYNAME_LIST).value.toString()
      },
      monthlyRecurrencePatternSetting: {
        useOrdinalDay: false,
        day: momentTimezone()._d.getDate(),
        recurrenceMonth: 1,
        ordinalDay: Math.round(momentTimezone()._d.getDate() / 7),
        ordinalDayValue: momentTimezone()._d.getDay() + 1,
        ordinalRecurrenceMonth: 0
      },
      yearlyRecurrencePatternSetting: {
        recurrenceYear: 1,
        useOrdinalDay: false,
        monthValue: momentTimezone()._d.getMonth() + 1,
        day: momentTimezone()._d.getDate(),
        ordinalDay: Math.round(momentTimezone()._d.getDate() / 7),
        ordinalDayValue: momentTimezone()._d.getDay() + 1,
        ordinalMonthValue: momentTimezone()._d.getMonth() + 1
      },
      isEndless: null,
      occurrence: 0,
      endDate: null,
      deliveryType: null,
      deliveryMethod: null,
      deliveryMethodView: function() {
        if (this.deliveryMethod === Constant.DELIVERY_METHOD_VALUES.Attachment) {
          return String.format(Constant.DELIVERY_METHOD_VIEW_PATTERN, this.deliveryMethod, this.exportAttachmentType);
        }
        return this.deliveryMethod;
      },
      exportFileType: null,
      exportAttachmentType: null,
      emailSubject: null,
      emailBody: null,
      subscriptionFilterFields: null,
      subscriptionCommonFilterFields: null,
      reportId: null,
      dashboardId: null,
      createdBy: null,
      createdById: null,
      id: null,
      state: null,
      modified: null,
      dateTimeNow: null,
      version: null,
      isSubscription: true,
      recipients: null,
      lastSuccessfulRun: null,
      nextScheduledRun: null,

      emailTemplates: null,
      reportFilter: null,
      isEndAfter: false,
      isEndBy: false,
      isEdit: false,
      selectedValue: false,

      // For UI logic.
      currentTab: Constant.SCHEDULER_TAB.key
    };

    m.extendObservable(trackable(this), props);
    this.populate(data, emailTemplates, reportFilter);
  }

  /**
   * Populate information.
   * @param {Object} data
   * @param {Array} emailTemplates
   * @param {Object} reportFilter
   */
  SubscriptionDetails.prototype.populate = m.action(function(data, emailTemplates, reportFilter) {
    data = data || {};

    this.name = data.name;
    this.schedule = data.schedule;
    this.filterValueSelection = data.filterValueSelection;
    this.type = data.type;
    this.timeZoneName = data.timeZoneName;
    this.timeZoneValue = data.timeZoneValue;
    this.startDate = data.startDate;
    this.startTime = data.startTime;
    this.recurrenceType = data.recurrenceType;
    this.recurrencePattern = data.recurrencePattern;
    this.recurrencePatternSetting = data.recurrencePatternSetting || {};
    this.isEndless = data.isEndless;
    this.occurrence = data.occurrence;
    this.endDate = data.endDate;
    this.deliveryType = data.deliveryType;
    this.deliveryMethod = data.deliveryMethod;
    this.exportFileType = data.exportFileType;
    this.exportAttachmentType = data.exportAttachmentType;
    this.emailSubject = data.emailSubject;
    this.emailBody = this.replaceBrTagInEmailBody(data.emailBody);
    this.subscriptionFilterFields = data.subscriptionFilterFields || [];
    this.subscriptionCommonFilterFields = data.subscriptionCommonFilterFields || [];
    this.reportId = data.reportId;
    this.dashboardId = data.dashboardId;
    this.createdBy = data.createdBy || '';
    this.createdById = data.createdById;
    this.id = data.id || uniqueId('newInstance_');
    this.state = data.state;
    this.modified = data.modified;
    this.dateTimeNow = data.dateTimeNow;
    this.version = data.version;
    this.isSubscription = data.isSubscription;
    this.recipients = data.recipients;
    this.lastSuccessfulRun = data.lastSuccessfulRun;
    this.nextScheduledRun = data.nextScheduledRun;

    this.emailTemplates =
      emailTemplates &&
      emailTemplates.map(
        function(template) {
          template.value = this.replaceBrTagInEmailBody(template.value);
          return template;
        }.bind(this)
      );
    this.reportFilter = reportFilter;
    this.isEndBy = !!data.endDate && data.occurrence === 0;
    this.isEndAfter = !!data.occurrence;
    data.isEdit && (this.isEdit = data.isEdit);

    data.dailyRecurrencePatternSetting && (this.dailyRecurrencePatternSetting = data.dailyRecurrencePatternSetting);
    data.weeklyRecurrencePatternSetting && (this.weeklyRecurrencePatternSetting = data.weeklyRecurrencePatternSetting);
    data.monthlyRecurrencePatternSetting &&
      (this.monthlyRecurrencePatternSetting = data.monthlyRecurrencePatternSetting);
    data.yearlyRecurrencePatternSetting && (this.yearlyRecurrencePatternSetting = data.yearlyRecurrencePatternSetting);

    this.updateRecurrencePatternSetting(data);

    this.resetDirty();
  });

  /**
   * Update recurrence pattern setting
   * @param {Object} data
   */
  SubscriptionDetails.prototype.updateRecurrencePatternSetting = function(data) {
    if (this.isEdit) {
      var recurrencePatternConstants = ScheduleConstants.RECURRENCE_PATTERN;
      switch (data.recurrencePattern) {
        case recurrencePatternConstants[0].value: // Daily
          this.dailyRecurrencePatternSetting = data.recurrencePatternSetting;
          break;
        case recurrencePatternConstants[1].value: // Weekly
          this.weeklyRecurrencePatternSetting = data.recurrencePatternSetting;
          break;
        case recurrencePatternConstants[2].value: // Monthly
          this.monthlyRecurrencePatternSetting = data.recurrencePatternSetting;
          break;
        case recurrencePatternConstants[3].value: // Yearly
          this.yearlyRecurrencePatternSetting = data.recurrencePatternSetting;
          break;
      }
      this.markDirty();
    }
  };

  /**
   * Update property using key
   * @param {String} key
   * @param {Object} value
   */
  SubscriptionDetails.prototype.updateProperty = function(key, value) {
    this[key] = value;
    this.markDirty();
  };

  /**
   * Check dirty status.
   * @return {boolean} dirty status
   */
  SubscriptionDetails.prototype.checkDirty = function() {
    return this.isDirty;
  };

  /**
   * Clean dirty status.
   */
  SubscriptionDetails.prototype.cleanDirty = function() {
    this.resetDirty();
  };

  /**
   * Init data
   * @return {object} SubscriptionDetails
   */
  SubscriptionDetails.prototype.init = m.action(function() {
    var recurrenceList = ScheduleConstants.RECURRENCE_REPORTLIST;
    var tz = this.timezone();

    this.type = this.isSubscription
      ? SCHEDULE_SEARCH_CONSTANTS.INSTANCE_TYPE_VALUE.SUBSCRIBE_REPORTING_ITEM
      : DashboardConstant.SCHEDULE_REPORT;
    this.timeZoneName = tz.label;
    this.timeZoneValue = tz.value;
    this.startDate = momentTimezone().format(DATE_FORMAT);
    this.startTime = null;
    this.recurrenceType = recurrenceList[0].value;
    this.isEndless = true;
    this.isEndAfter = !!this.occurrence;
    this.isEndBy = !!this.endDate;
    this.deliveryType = Constant.DELIVERY_TYPE[0].value;
    this.deliveryMethod = Constant.DELIVERY_TYPE[0].deliveryMethod[0].value;
    this.emailSubject = this.reportId ? Constant.EMAIL_SUBJECT.REPORT : Constant.EMAIL_SUBJECT.DASHBOARD;
    this.getEmailBody();
    this.state = this.id.indexOf('newInstance_') >= 0 ? ObjectStateConstants.INSERT : ObjectStateConstants.UPDATE;
    this.endDate = momentTimezone()
      .add(3, 'month')
      .format(DATE_FORMAT);
    this.recurrencePattern = 1;
    this.recurrencePatternSetting = this.weeklyRecurrencePatternSetting;
    this.isEdit = false;

    return this;
  });

  /**
   * Get timezone object
   * @return {object} timezone
   */
  SubscriptionDetails.prototype.timezone = function() {
    var props = this || {};
    var timeZoneValue;
    if (props.timeZoneValue) {
      timeZoneValue = props.timeZoneValue;
    } else {
      timeZoneValue = TimezoneConstant.TIMEZONE[0].value;
    }

    return find(function(tz) {
      return isEqual(tz.value, timeZoneValue) || (tz.abr && indexOf(timeZoneValue, tz.abr) > -1);
    }, TimezoneConstant.TIMEZONE);
  };

  /**
   * Get email body
   */
  SubscriptionDetails.prototype.getEmailBody = function() {
    var deliveryMethod = this.deliveryMethod;
    var matchedBody = find(function(item) {
      return deliveryMethod === item.key;
    }, this.emailTemplates);

    this.emailBody = matchedBody && this.replaceBrTagInEmailBody(matchedBody.value);
    this.markDirty();
  };

  /**
   * Build data for saving
   */
  SubscriptionDetails.prototype.buildDataForSaving = function() {
    this.buildRecurrencePatternSetting();
    this.syncRangeRecurrenceData();
    this.buildEmailTemplateForSave();
  };

  /**
   * Build recurrence pattern setting from UI values.
   */
  SubscriptionDetails.prototype.buildRecurrencePatternSetting = function() {
    var recurrencePatternConstants = ScheduleConstants.RECURRENCE_PATTERN;
    switch (this.recurrencePattern) {
      case recurrencePatternConstants[0].value:
        this.recurrencePatternSetting = this.dailyRecurrencePatternSetting;
        break;
      case recurrencePatternConstants[1].value:
        this.recurrencePatternSetting = this.weeklyRecurrencePatternSetting;
        break;
      case recurrencePatternConstants[2].value:
        this.recurrencePatternSetting = this.monthlyRecurrencePatternSetting;
        break;
      case recurrencePatternConstants[3].value:
        this.recurrencePatternSetting = this.yearlyRecurrencePatternSetting;
        break;
    }
  };

  /**
   * Synchronize range of recurrence data before saving
   */
  SubscriptionDetails.prototype.syncRangeRecurrenceData = function() {
    !this.isEndAfter && (this.occurrence = 0);
    !this.isEndBy && (this.endDate = null);
    if (this.isEndless) {
      this.occurrence = 0;
      this.endDate = null;
    }
    this.markDirty();
  };

  /**
   * Replace new line characer by html <Br> tag
   */
  SubscriptionDetails.prototype.buildEmailTemplateForSave = function() {
    var reg = new RegExp('\\n', 'g');
    var emailBody = this.emailBody;
    emailBody && (this.emailBody = emailBody.replace(reg, '<br>'));
    this.markDirty();
  };

  /**
   * Add recipients
   * @param {Object} List of user account detail object
   */
  SubscriptionDetails.prototype.addRecipients = function(userAccountList) {
    this.recipients = chain(userAccountList)
      .flatMap(function(userAccount) {
        return userAccount.emailAddress;
      })
      .join(';')
      .value();
    this.markDirty();
  };

  /**
   * Get recipients
   * @param {Object} List of user account detail object
   */
  SubscriptionDetails.prototype.getRecipients = function() {
    if (!this.recipients) return null;

    var recipientList = isArray(this.recipients) ? this.recipients : compact(split(';', this.recipients));
    return map(function(recipient) {
      return { emailAddress: recipient };
    }, recipientList);
  };

  /**
   * Omit data for saving
   * @return {object} subscription details
   */
  SubscriptionDetails.prototype.omitData = function() {
    return omit(
      [
        'dailyRecurrencePatternSetting',
        'weeklyRecurrencePatternSetting',
        'monthlyRecurrencePatternSetting',
        'yearlyRecurrencePatternSetting',
        'reportFilter'
      ],
      this
    );
  };

  /**
   * Update data for edit mode
   * @return {object} data
   */
  SubscriptionDetails.prototype.updateDataForEditMode = m.action(function(data) {
    (this.emailTemplates = data.emailTemplates), (this.state = data.state);
    this.isEdit = true;
    this.isSubscription = data.isSubscription;
    this.markDirty();
  });

  /**
   * Update data selected value
   * @return {boolean} value
   */
  SubscriptionDetails.prototype.updateSelectedValue = function(value) {
    this.selectedValue = value;
    this.markDirty();
  };

  /**
   * Add recipients
   * @param {Object} List of user account detail object
   */
  SubscriptionDetails.prototype.addMoreRecipients = function(userAccountList) {
    var currentRecipients = isArray(this.recipients) ? this.recipients : compact(split(';', this.recipients));

    var newRecipients = map('emailAddress', userAccountList);

    this.recipients = filter(function(item) {
      return !isEmpty(item);
    }, union(currentRecipients, newRecipients)).join(';');
    this.markDirty();
  };

  /**
   * Update current tab value.
   * @param {Object} value
   */
  SubscriptionDetails.prototype.setCurrentTab = function(value) {
    this.currentTab = value;
  };

  /**
   * Replace all html <br> tag in email by newline character
   * @param email
   * @returns {*|string}
   */
  SubscriptionDetails.prototype.replaceBrTagInEmailBody = function(email) {
    return (
      email &&
      email
        .split(/<br\/?>/)
        .map(trim)
        .join('\n')
    );
  };

  module.exports = SubscriptionDetails;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.SubscriptionDetails.js